import * as React from "react";
import { RouteComponentProps } from "react-router";
import {
  AppNotificationMessage,
  AppNotificationType,
} from "src/domain/model/app_notification_message";
import { FrontDate } from "src/domain/model/frontDate";
import { createProjectAPI } from "src/infra/api/service/project_api";
import { createUserAPI } from "src/infra/api/service/user_api";
import BtnCorporateLarge from "src/presentation/common/atom/btn/btn_corporate_large";
import FormFieldWrapper from "src/presentation/common/atom/wrapper/form_field_wrapper";
import SubmitButtonWrapper from "src/presentation/common/atom/wrapper/submit_btn_wrapper/submit_btn_wrapper";
import AppNotificationWithTexts from "src/presentation/common/organism/app_notification_with_texts";
import styled from "styled-components";
import { routeConfig } from "../../config/language/ja-JP/presentation/route_text";
import { createApiClient, IApiClient } from "../../infra/api/client";
import { api } from "../../infra/api/rpc/api";
import PageWrapper from "../common/atom/wrapper/page_wrapper";
import Breadcrumbs from "../common/molecular/breadcrumbs/breadcrumbs";
import { TBreadcrumbItem } from "../type/common";

const apiClient: IApiClient = createApiClient();
const projectAPI = createProjectAPI(apiClient);
const userAPI = createUserAPI(apiClient);

interface IProps extends RouteComponentProps<{}> {}

const CustomerProjectSharingForm: React.FC<IProps> = (props) => {
  const breadcrumbItems: TBreadcrumbItem[] = [
    routeConfig.clientRoot,
    { text: `案件シェアリング` },
    { text: `申し込みフォーム` },
  ];
  const [params, setParams] = React.useState<api.ISharingProjectRequest>({});
  const [messages, setMessages] = React.useState<AppNotificationMessage[]>([]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      projectAPI.sharing(params).then(() => {
        setParams({});

        setTimeout(() => {
          props.history.push("/customer/project-sharing?success=true");
        }, 500);
      });
    } catch (e) {
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.ERROR,
          "エラーが発生しました",
          FrontDate.fromEmpty()
        ),
      ]);
    }
  };

  React.useEffect(() => {
    try {
      userAPI.getMe().then((user) => {
        setParams((prevParams) => ({
          ...prevParams,
          customerName: user.customer!!.name,
          customerEmail: user.email,
          customerPhoneNumber: user.customer!!.phoneNumber,
        }));
      });
    } catch (e) {
      setMessages([
        AppNotificationMessage.from(
          "",
          AppNotificationType.ERROR,
          "エラーが発生しました",
          FrontDate.fromEmpty()
        ),
      ]);
    }
  }, []);

  return (
    <React.Fragment>
      {messages.length > 0 && (
        <AppNotificationWithTexts
          onClose={(id: string) =>
            (event: any): void => {
              setMessages([]);
            }}
          appNotificationMessages={messages}
        />
      )}
      <PageWrapper>
        <Breadcrumbs breadcrumbItems={breadcrumbItems} />

        <form
          name="CustomerProjectSharingForm"
          onSubmit={handleSubmit}
          style={{ maxWidth: 600, margin: "0 auto" }}
        >
          <Header>紹介者の情報</Header>
          <FormFieldWrapper>
            <Label>エキスパート名</Label>
            <Required />
            <div>
              <Input
                type="text"
                value={params.customerName!!}
                onChange={(e) => {
                  setParams({ ...params, customerName: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>メールアドレス</Label>
            <Required />
            <div>
              <Input
                type="text"
                value={params.customerEmail!!}
                onChange={(e) => {
                  setParams({ ...params, customerEmail: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>電話番号</Label>
            <Required />
            <div>
              <Input
                type="text"
                value={params.customerPhoneNumber!!}
                onChange={(e) => {
                  setParams({ ...params, customerPhoneNumber: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>

          <Header>紹介先企業の情報</Header>
          <FormFieldWrapper>
            <Label>紹介先企業名</Label>
            <Required />
            <div>
              <Input
                type="text"
                value={params.referralCompanyName!!}
                placeholder="紹介株式会社"
                onChange={(e) => {
                  setParams({ ...params, referralCompanyName: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>担当者名</Label>
            <Required />
            <div>
              <Input
                type="text"
                value={params.mayConcernName!!}
                placeholder="山田 太郎"
                onChange={(e) => {
                  setParams({ ...params, mayConcernName: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>部署</Label>
            <div>
              <Input
                type="text"
                value={params.department!!}
                placeholder="ビジネス統括部"
                onChange={(e) => {
                  setParams({ ...params, department: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>役職</Label>
            <div>
              <Input
                type="text"
                value={params.position!!}
                placeholder="部長"
                onChange={(e) => {
                  setParams({ ...params, position: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>担当者メールアドレス</Label>
            <div>
              <Input
                type="text"
                value={params.mayConcernEmail!!}
                placeholder="yamada.taro@libera-inc.com"
                onChange={(e) => {
                  setParams({ ...params, mayConcernEmail: e.target.value });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>担当者電話番号</Label>
            <div>
              <Input
                type="text"
                value={params.mayConcernPhoneNumber!!}
                placeholder="0801234567"
                onChange={(e) => {
                  setParams({
                    ...params,
                    mayConcernPhoneNumber: e.target.value,
                  });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>案件概要</Label>
            <Required />
            <div>
              <Input
                type="text"
                value={params.projectTitle!!}
                placeholder="Webマーケティングの改善支援"
                onChange={(e) => {
                  setParams({
                    ...params,
                    projectTitle: e.target.value,
                  });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>緊急度</Label>
            <Required />
            <div>
              <Select
                value={params.priority ? params.priority.toString() : ""}
                onChange={(e) => {
                  let priority =
                    api.SharingProjectRequest.Priority.Priority_High;
                  if (e.target.value === "1") {
                    priority = api.SharingProjectRequest.Priority.Priority_High;
                  } else if (e.target.value === "2") {
                    priority =
                      api.SharingProjectRequest.Priority.Priority_Middle;
                  } else if (e.target.value === "3") {
                    priority = api.SharingProjectRequest.Priority.Priority_Low;
                  } else {
                    return;
                  }

                  setParams({
                    ...params,
                    priority,
                  });
                }}
              >
                <option>---</option>
                <option value="1">高</option>
                <option value="2">中</option>
                <option value="3">低</option>
              </Select>
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>その他</Label>
            <div>
              <Input
                type="text"
                value={params.message!!}
                onChange={(e) => {
                  setParams({
                    ...params,
                    message: e.target.value,
                  });
                }}
              />
            </div>
          </FormFieldWrapper>
          <FormFieldWrapper>
            <Label>紹介者とLibera担当者の事前打ち合わせの必要性の有無</Label>
            <Required />
            <div>
              <Select
                value={
                  params.isNeedPreliminaryMeeting !== undefined
                    ? params.isNeedPreliminaryMeeting
                      ? "1"
                      : "2"
                    : ""
                }
                onChange={(e) => {
                  let isNeed = false;
                  if (e.target.value === "1") {
                    isNeed = true;
                  } else if (e.target.value === "2") {
                    isNeed = false;
                  } else {
                    return;
                  }

                  setParams({
                    ...params,
                    isNeedPreliminaryMeeting: isNeed,
                  });
                }}
              >
                <option>---</option>
                <option value="1">はい</option>
                <option value="2">いいえ</option>
              </Select>
            </div>
          </FormFieldWrapper>

          <SubmitButtonWrapper>
            <BtnCorporateLarge
              name="CustomerProjectSharingForm-submit"
              type="submit"
              widthSize="200px"
              disabled={
                !params.customerName ||
                !params.customerEmail ||
                !params.customerPhoneNumber ||
                !params.referralCompanyName ||
                !params.mayConcernName ||
                !params.projectTitle ||
                !params.priority ||
                !params.isNeedPreliminaryMeeting
              }
            >
              送信
            </BtnCorporateLarge>
          </SubmitButtonWrapper>
        </form>
      </PageWrapper>
    </React.Fragment>
  );
};

export default CustomerProjectSharingForm;

const Header = styled.h2`
  margin-bottom: 20px;
  margin-top: 40px;
`;

const Label = styled.label`
  display: inline-block;
  margin-bottom: 10px;
`;

const Input = styled.input`
  width: 100%;
  height: 50px;
  padding: 0 10px;
`;

const Required = styled.span`
  margin-left: 5px;
  background-color: #ce4141;
  width: 5px;
  height: 5px;
  display: inline-block;
  border-radius: 50%;
  vertical-align: middle;
`;

const Select = styled.select`
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  width: 100%;
  padding: 8px 20px;
  border: 1px solid #a4a4a4;
  box-sizing: border-box;
  border-radius: 3px;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 23px;
  background-color: #ffffff;
  color: #333333;
  background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAGCAYAAAD37n+BAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAACNSURBVHgBfY8xDsIwDEXr9AKsuQVVlJ3eAEZuxm1YYYjiIzAlsNEDJKl/VVVR1dZLHP/3v2xqpIwxT3kuzXGxc65T6FJKt1LKZ4+EJkyPfjIw8z/n3G+ZMIMGBn+qRWvtWUSsd5phBHUCL0FtbQghfLXWPyK6TmlEd+/9q2ba9QoxRhbToJR6y5GPtT4CsDRIeMuP1hEAAAAASUVORK5CYII=);
  background-repeat: no-repeat;
  background-size: 10px 6px;
  background-position: right 10px center;
`;



// WEBPACK FOOTER //
// ./src/presentation/container/customer_project_sharing_form.tsx